import { Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router';
import { setRouter } from './history';
import Home from 'components/Home';
import lazyWithReload from './lazyWithReload';

// Route-level code splitting, mirroring the pre-Vite chunk boundaries.
const CoreLayout = lazyWithReload(() => import('components/CoreLayout'));
const Products = lazyWithReload(() => import('components/Products'));
const AllAboutMe = lazyWithReload(() => import('components/Products/components/AllAboutMe'));
const EducationPlanner = lazyWithReload(() => import('components/Products/components/EducationPlanner'));
const Support = lazyWithReload(() => import('components/Support'));
const SupportHome = lazyWithReload(() => import('components/Support/components/SupportHome'));
const LessonPlans = lazyWithReload(() => import('components/Support/components/LessonPlans'));
const Webinars = lazyWithReload(() => import('components/Support/components/Webinars'));
const Resources = lazyWithReload(() => import('components/Support/components/Resources'));
const ResourcesSelectProduct = lazyWithReload(() => import('components/Support/components/Resources/components/SelectProduct'));
const ProductResources = lazyWithReload(() => import('components/Support/components/Resources/components/ProductResources'));
const Resource = lazyWithReload(() => import('components/Support/components/Resources/components/Resource'));
const Videos = lazyWithReload(() => import('components/Support/components/Videos'));
const VideosSelectProduct = lazyWithReload(() => import('components/Support/components/Videos/components/SelectProduct'));
const ProductVideos = lazyWithReload(() => import('components/Support/components/Videos/components/ProductVideos'));
const Video = lazyWithReload(() => import('components/Support/components/Videos/components/Video'));
const About = lazyWithReload(() => import('components/About'));
const Careers = lazyWithReload(() => import('components/Careers'));
const Privacy = lazyWithReload(() => import('components/Privacy'));
const Terms = lazyWithReload(() => import('components/Terms'));

// Injects react-router v3-style props (params, location) into class components.
function routeElement(Component, { outlet = false } = {}) {
  function Wrapped() {
    const params = useParams();
    const location = useLocation();
    return (
      <Component params={params} location={location}>
        {outlet ? <Outlet /> : null}
      </Component>
    );
  }
  return <Wrapped />;
}

function ExternalRedirect({ href }) {
  useEffect(() => {
    window.location.href = href;
  }, [href]);
  return null;
}

ExternalRedirect.propTypes = {
  href: PropTypes.string.isRequired
};

export const createAppRouter = () => {
  const routerInstance = createBrowserRouter([
  {
    path: '/',
    element: (
      <Suspense fallback={null}>
        {routeElement(CoreLayout, { outlet: true })}
      </Suspense>
    ),
    children: [
      { index: true, element: routeElement(Home) },
      {
        path: 'products',
        element: routeElement(Products, { outlet: true }),
        children: [
          { index: true, element: routeElement(AllAboutMe) },
          { path: 'allaboutme', element: routeElement(AllAboutMe) },
          { path: 'educationplanner', element: routeElement(EducationPlanner) }
        ]
      },
      {
        path: 'support',
        element: routeElement(Support, { outlet: true }),
        children: [
          { index: true, element: routeElement(SupportHome) },
          { path: 'webinars', element: routeElement(Webinars) },
          { path: 'lessonplans', element: routeElement(LessonPlans) },
          {
            path: 'resources',
            element: routeElement(Resources, { outlet: true }),
            children: [
              { index: true, element: routeElement(ResourcesSelectProduct) },
              { path: ':product/:resourceId/:language?', element: routeElement(Resource) },
              { path: ':product', element: routeElement(ProductResources) }
            ]
          },
          {
            path: 'videos',
            element: routeElement(Videos, { outlet: true }),
            children: [
              { index: true, element: routeElement(VideosSelectProduct) },
              { path: ':product/:videoId', element: routeElement(Video) },
              { path: ':product', element: routeElement(ProductVideos) }
            ]
          }
        ]
      },
      { path: 'about', element: routeElement(About) },
      { path: 'careers', element: routeElement(Careers) },
      { path: 'privacy', element: routeElement(Privacy) },
      { path: 'terms', element: routeElement(Terms) },

      // Legacy redirects (paths are matched case-insensitively, so aam/AAM are covered)
      { path: 'start', element: <ExternalRedirect href="https://app.myblueprint.ca/start" /> },
      { path: 'commencer', element: <ExternalRedirect href="https://app.myblueprint.ca/start?lang=fr-CA" /> },
      { path: 'resources', element: <Navigate to="/support/resources" replace /> },
      { path: 'shsm-resources', element: <Navigate to="/support/resources" replace /> },
      { path: 'support/educator-resources', element: <Navigate to="/support/resources" replace /> },
      { path: 'webinars', element: <Navigate to="/support/webinars" replace /> },
      { path: 'support/webinar-training', element: <Navigate to="/support/webinars" replace /> },
      { path: 'set/videos', element: <Navigate to="/support/videos" replace /> },
      { path: 'all-about-me-portfolio', element: <Navigate to="/products/allaboutme" replace /> },
      { path: 'aam', element: <Navigate to="/products/allaboutme" replace /> },

      { path: ':schoolBoardId', element: routeElement(Home) }
    ]
  }
  ]);
  setRouter(routerInstance);
  return routerInstance;
};
